// src/components/AdminPortal/UserManagement/Privileges.js
import { useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import Axios from "../../../utils/axios";
import "./Privilege.css";
import ContentLoader from "../../../global/Content/ContentLoader";

const Privileges = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState("");
  const [privileges, setPrivileges] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [formData, setFormData] = useState({
    privilegeName: "",
    type: "",
    description: "",
    applicationId: "",
  });

  const [errors, setErrors] = useState({});

  // Fetch all applications
  const fetchApplications = async () => {
    try {
      const response = await Axios.get(`/api/application/getAllApplications`);
      setApplications(response?.data || []);
      if (response?.data?.length > 0) {
        setSelectedApplicationId(response.data[0].id); // default first application
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load applications"
      );
    }
  };

  // Fetch privileges for selected application
  const fetchPrivileges = async () => {
    if (!selectedApplicationId) return;
    setIsRefreshing(true);
    try {
      const response = await Axios.get(
        `/api/applicationPrivilege/getAllApplicationPrivilegesByApplicationId?applicationId=${selectedApplicationId}`
      );
      setPrivileges(response?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Create privilege
  const createPrivilege = async (data) => {
    try {
      const response = await Axios.post(
        `/api/applicationPrivilege/createApplicationPrivilege`,
        data
      );
      return response.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    fetchPrivileges();
  }, [selectedApplicationId]);

  const filteredPrivileges = privileges.filter(
    (priv) =>
      priv.privilegeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      priv.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      priv.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    let newErrors = {};
    if (!formData.privilegeName) newErrors.privilegeName = "Name is required";
    if (!formData.type) newErrors.type = "Type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNew = () => {
    setFormData({
      privilegeName: "",
      type: "",
      description: "",
      applicationId: selectedApplicationId,
    });
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    console.log("hello");
    e.preventDefault();

    setIsLoading(true);
    try {
      const newPriv = await createPrivilege(formData);
      setPrivileges([...privileges, newPriv]);
      toast.success("Privilege added successfully!");
      setIsModalOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="table-container">
      <h2>Application Privileges</h2>
      <br />

      {/* Application selector */}
      <div className="app-dropdown-container">
        <label htmlFor="application-select" className="app-dropdown-label">
          Application
        </label>
        <select
          id="application-select"
          className="app-dropdown"
          value={selectedApplicationId}
          onChange={(e) => setSelectedApplicationId(e.target.value)}
        >
          <option value="">-- Select Application --</option>
          {applications.map((app) => (
            <option key={app.id} value={app.id}>
              {app.applicationName}
            </option>
          ))}
        </select>
      </div>

      {/* Search and New */}
      <div className="search-and-new">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button onClick={() => setSearchTerm("")} className="clear-button">
          Clear
        </button>
        {/* <button onClick={handleNew} className="new-button" disabled={isLoading}>
          <FiPlus /> New
        </button> */}
      </div>

      {isRefreshing && !privileges.length ? (
        <div className="loading">
          <ContentLoader />
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              {/* <th>Type</th> */}
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {filteredPrivileges.map((priv) => (
              <tr key={priv.id}>
                <td>{priv.privilegeName}</td>
                {/* <td>{priv.type}</td> */}
                <td>{priv.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>New Privilege</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="modal-close-button"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name*</label>
                <input
                  name="privilegeName"
                  value={formData.privilegeName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                {errors.privilegeName && (
                  <p className="text-danger">{errors.privilegeName}</p>
                )}
              </div>
              {/* <div className="form-group">
                <label>Type*</label>
                <input name="type" value={formData.type} onChange={handleChange} disabled={isLoading} />
                {errors.type && <p className="text-danger">{errors.type}</p>}
              </div> */}
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button type="submit" disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Privileges;
